import type { EncryptedField, EncryptedPayload } from './types'

interface EncryptableCommand {
  readonly userPrompt: string | null
  readonly userAnswers: Record<string, unknown> | null
}

export interface EncryptedCommandFields {
  readonly userPrompt: EncryptedField | null
  readonly userAnswers: EncryptedField | null
}

const toEncryptedField = (payload: EncryptedPayload): EncryptedField => ({
  ciphertext: payload.ciphertext,
  iv: payload.iv,
})

export const encryptCommandFields = async (
  command: EncryptableCommand,
  encrypt: (data: string | Record<string, unknown>) => Promise<EncryptedPayload>,
): Promise<EncryptedCommandFields | null> => {
  try {
    const [encryptedPrompt, encryptedAnswers] = await Promise.all([
      command.userPrompt === null
        ? Promise.resolve(null)
        : encrypt(command.userPrompt)
            .then(toEncryptedField)
            .catch((err: unknown) => {
              throw new Error(`userPrompt: ${err instanceof Error ? err.message : 'Unknown error'}`)
            }),
      command.userAnswers === null
        ? Promise.resolve(null)
        : encrypt(command.userAnswers)
            .then(toEncryptedField)
            .catch((err: unknown) => {
              throw new Error(
                `userAnswers: ${err instanceof Error ? err.message : 'Unknown error'}`,
              )
            }),
    ])

    if (encryptedPrompt === null && encryptedAnswers === null) return null

    return { userPrompt: encryptedPrompt, userAnswers: encryptedAnswers }
  } catch (error) {
    throw new Error(
      `Failed to encrypt command: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}
